import { assertIsDefined } from 'util/assert.ts';
import { get } from 'api/utils';
import ENDPOINTS from 'api/utils/endpoints.ts';
import useQueryKeys from 'api/utils/useQueryKeys';
import { useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import { useAtom } from 'jotai';
import { userAtom } from 'store/userAtom.ts';
import { useQuery } from '@tanstack/react-query';
import {
  leadsRevivalSchema,
  type LeadsRevivalResponse,
} from 'pages/Shoppers/LeadsRevival/api/useLeadsRevival/leadsRevivalSchema';
import { piiEnabledAtom, piiTokenAtom } from 'store/piiEnabledAtom';
import { toTitleCase } from 'util/formatters';

const titleCase = (value: string | undefined) => toTitleCase(value ?? '');

export type { LeadsRevivalResponse };

interface FetchLeadsRevivalProps {
  piiToken?: string;
  piiEnabled: boolean;
  token?: string;
  dealerId?: number;
}

async function fetchLeadsRevival({
  piiToken,
  piiEnabled,
  token,
  dealerId,
}: FetchLeadsRevivalProps) {
  assertIsDefined(dealerId);
  assertIsDefined(token);
  assertIsDefined(piiEnabled);

  const res = await get<LeadsRevivalResponse>(`${ENDPOINTS.idpPii}/${dealerId}`, {
    headers: { token, Authorization: piiEnabled ? (piiToken ?? '') : '' },
  });

  return {
    data: leadsRevivalSchema
      .parse({ data: Array.isArray(res?.data) ? res.data : [] })
      .data.map((row) => ({
        ...row,
        name: { ...row.name, name: titleCase(row.name.name) },
        address: { ...row.address, address: titleCase(row.address.address) },
        city: titleCase(row.city),
      })),
  };
}

export interface UseLeadsRevivalOptions {
  enabled?: boolean;
}

/**
 * useLeadsRevival — Revive shoppers: lost CRM leads Lotlinx reactivated (GET /idp/pii). The
 * endpoint has no shopper-type parameter; Reveal shoppers come from useCampaignOverviewShoppers.
 */

export default function useLeadsRevival(options?: UseLeadsRevivalOptions) {
  const [dealerId] = useSelectedDealerId();
  const [user] = useAtom(userAtom);
  const [piiToken] = useAtom(piiTokenAtom);
  const [piiEnabled] = useAtom(piiEnabledAtom);
  const { leadsRevivalKey } = useQueryKeys({ piiToken, piiEnabled });

  const enabled = (options?.enabled ?? true) && dealerId !== undefined && user?.token !== undefined;

  return useQuery({
    queryKey: leadsRevivalKey,
    queryFn: () =>
      fetchLeadsRevival({
        piiToken: piiToken ?? undefined,
        piiEnabled,
        token: user?.token,
        dealerId,
      }),
    enabled,
  });
}
